import { FC, useContext, useMemo, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Button, Dropdown, IDropdownItem } from '@smartshell/ui-kit';

import { useOnClickOutside } from 'hooks';
import { LocalizationContext } from 'localization';
import s from 'pages/Clients/Clients.module.scss';
import {} from 'pages/Clients/interfaces';
import {
  CHANGE_USERS_GROUP,
  TChangeUsersGroupRequest,
  TChangeUsersGroupResponse,
} from 'schemas';

import { TClientsTableProps } from './interfaces';

export const GroupControls: FC<TClientsTableProps> = ({
  groups,
  selectedClientsUUIDs,
  setSelectedClientsUUIDs,
  data,
}) => {
  const { t } = useContext(LocalizationContext);
  const groupControlsMenuRef = useRef(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [changeUserGroup] = useMutation<
    TChangeUsersGroupResponse,
    TChangeUsersGroupRequest
  >(CHANGE_USERS_GROUP, {
    onCompleted: () => setSelectedClientsUUIDs([]),
    update: (cache, _, { variables }) => {
      const clients = data?.clients;
      if (variables && clients && groups) {
        const { group_uuid, users_uuids } = variables.input;
        const uuidSet = new Set(users_uuids);
        const group = groups.find(({ uuid }) => uuid === group_uuid);

        cache.modify({
          fields: {
            clients: () => ({
              ...clients,
              data: clients.data.map((client) =>
                uuidSet.has(client.uuid) ? { ...client, group } : client
              ),
            }),
          },
        });
      }
    },
  });

  const groupsDropdownItems: IDropdownItem<string>[] = useMemo(() => {
    if (!groups) return [];
    const selectedClientsInGroup: Record<string, number> = {};
    const setClients = new Set(selectedClientsUUIDs);

    data?.clients.data.forEach(({ group, uuid: clientUUID }) => {
      if (setClients.has(clientUUID) && group) {
        if (!selectedClientsInGroup[group.uuid]) {
          selectedClientsInGroup[group.uuid] = 0;
        }

        selectedClientsInGroup[group.uuid] += 1;
      }
    });

    return groups.map(({ uuid, title }) => ({
      value: uuid,
      title,
      disabled:
        (selectedClientsInGroup[uuid] &&
          selectedClientsInGroup[uuid] === setClients.size) ||
        false,
    }));
  }, [groups, selectedClientsUUIDs, data]);

  const selectedClientsLength = selectedClientsUUIDs.length;

  useOnClickOutside(groupControlsMenuRef, () => setIsDropdownOpen(false));

  return (
    <div className={s.groupControls}>
      <Button
        className={s.addUsersToGroupButton}
        color="red"
        variant="secondary"
        onClick={() =>
          changeUserGroup({
            variables: {
              input: {
                group_uuid: '',
                users_uuids: selectedClientsUUIDs,
              },
            },
          })
        }
      >
        {t('clients.top.remove.group')}
      </Button>
      <div ref={groupControlsMenuRef}>
        <Button
          className={s.addUsersToGroupButton}
          onClick={() => setIsDropdownOpen((open) => !open)}
          variant="secondary"
          color="blue"
        >
          {isDropdownOpen && (
            <Dropdown
              items={groupsDropdownItems}
              className={s.dropdown}
              placeholder={t('clients.group.empty')}
              onChange={(uuid) => {
                if (selectedClientsLength > 0) {
                  changeUserGroup({
                    variables: {
                      input: {
                        group_uuid: uuid,
                        users_uuids: selectedClientsUUIDs,
                      },
                    },
                  });
                }
              }}
              close={() => setIsDropdownOpen(false)}
            />
          )}
          {t('clients.top.add.group')}
        </Button>
      </div>
    </div>
  );
};
